var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { isAdminUser, isAnonymousUser, isRegisteredUser } from "@/api";
import { useUserLocalStorage } from "@/composables/userLocalStorage";
import { useHistoryStore } from "@/stores/historyStore";
import { addFavoriteToolQuery, getCurrentUser, removeFavoriteToolQuery, setCurrentThemeQuery, } from "@/stores/users/queries";
export const useUserStore = defineStore("userStore", () => {
    const currentUser = ref(null);
    const currentPreferences = ref(null);
    const preferredListViewMode = useUserLocalStorage("user-store-preferred-list-view-mode", "grid", currentUser);
    let loadPromise = null;
    function $reset() {
        currentUser.value = null;
        currentPreferences.value = null;
        loadPromise = null;
    }
    const isAdmin = computed(() => {
        return isAdminUser(currentUser.value);
    });
    const isAnonymous = computed(() => {
        return isAnonymousUser(currentUser.value);
    });
    const currentTheme = computed(() => {
        var _a, _b;
        return (_b = (_a = currentPreferences.value) === null || _a === void 0 ? void 0 : _a.theme) !== null && _b !== void 0 ? _b : null;
    });
    const currentFavorites = computed(() => {
        var _a;
        if ((_a = currentPreferences.value) === null || _a === void 0 ? void 0 : _a.favorites) {
            return currentPreferences.value.favorites;
        }
        else {
            return { tools: [] };
        }
    });
    const matchesCurrentUsername = computed(() => {
        return (username) => {
            return isRegisteredUser(currentUser.value) && currentUser.value.username === username;
        };
    });
    function setCurrentUser(user) {
        currentUser.value = user;
    }
    function loadUser(includeHistories = true) {
        if (!loadPromise) {
            loadPromise = getCurrentUser()
                .then((user) => __awaiter(this, void 0, void 0, function* () {
                if (isRegisteredUser(user)) {
                    currentUser.value = user;
                    currentPreferences.value = processUserPreferences(user);
                }
                else if (isAnonymousUser(user)) {
                    currentUser.value = user;
                }
                else if (user === null) {
                    currentUser.value = null;
                }
                if (includeHistories) {
                    const historyStore = useHistoryStore();
                    // load first few histories for user to start pagination
                    yield historyStore.loadHistories();
                }
            }))
                .catch((e) => {
                console.error("Failed to load user", e);
            })
                .finally(() => {
                loadPromise = null;
            });
        }
    }
    function setCurrentTheme(theme) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!currentUser.value || currentUser.value.isAnonymous) {
                return;
            }
            const currentTheme = yield setCurrentThemeQuery(currentUser.value.id, theme);
            if (currentPreferences.value) {
                currentPreferences.value.theme = currentTheme;
            }
        });
    }
    function addFavoriteTool(toolId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!currentUser.value || currentUser.value.isAnonymous) {
                return;
            }
            const tools = yield addFavoriteToolQuery(currentUser.value.id, toolId);
            setFavoriteTools(tools);
        });
    }
    function removeFavoriteTool(toolId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!currentUser.value || currentUser.value.isAnonymous) {
                return;
            }
            const tools = yield removeFavoriteToolQuery(currentUser.value.id, toolId);
            setFavoriteTools(tools);
        });
    }
    function setFavoriteTools(tools) {
        if (currentPreferences.value) {
            currentPreferences.value.favorites.tools = tools;
        }
    }
    function setPreferredListViewMode(view) {
        preferredListViewMode.value = view;
    }
    function processUserPreferences(user) {
        // Favorites are returned as a JSON string by the API
        const favorites = typeof user.preferences.favorites === "string" ? JSON.parse(user.preferences.favorites) : { tools: [] };
        return Object.assign(Object.assign({}, user.preferences), { favorites });
    }
    return {
        currentUser,
        currentPreferences,
        isAdmin,
        isAnonymous,
        currentTheme,
        currentFavorites,
        preferredListViewMode,
        loadUser,
        matchesCurrentUsername,
        setCurrentUser,
        setCurrentTheme,
        setPreferredListViewMode,
        addFavoriteTool,
        removeFavoriteTool,
        $reset,
    };
});
